import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Tracks list filters that arrive from a launch-page tile and that the page's own filter controls
 * cannot represent. `isActive` drives the "Filtered from dashboard" chip; `reset` removes those
 * params from the URL (the page resets its own local state alongside it).
 */
interface UseDashboardFiltersOptions {
  /** Params cleared on reset that do not by themselves mark the page as dashboard-filtered (e.g. status). */
  resetAlso?: readonly string[];
  /** Extra condition for filters that are only recognisable by value, e.g. a comma-separated status. */
  isActiveWhen?: (params: URLSearchParams) => boolean;
}

export function useDashboardFilters(keys: readonly string[], options: UseDashboardFiltersOptions = {}) {
  const { resetAlso = [], isActiveWhen } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const isActive = keys.some((key) => searchParams.has(key)) || (isActiveWhen?.(searchParams) ?? false);

  const reset = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    [...keys, ...resetAlso].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  }, [keys, resetAlso, searchParams, setSearchParams]);

  /** Removes one filter and leaves the others, so the user can narrow the list back down a step at a time. */
  const clear = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return { isActive, reset, clear };
}
